import { Col, Container, Row } from 'react-bootstrap'
import React, { useRef, useState } from 'react'
import { useGetAccountUserSettingsQuery, useUpdateAccountUserSettingsMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { TimezoneSelector } from '../../../../components/TimezoneSelector/TimezoneSelector'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { CurrentTimeDisplay } from '../../../../components/TimezoneSelector/CurrentTimeDisplay'
import { DEFAULT_TIMEZONE } from '../../../../components/TimezoneSelector/constants'

export const AccountTimezoneSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const [showChangesSaved, setShowChangesSaved] = useState<boolean>(false)
  const changesSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true },
  )
  const [updateAccountUserSettings, { isLoading: accountUserSettingsSaveLoading }] = useUpdateAccountUserSettingsMutation()

  const accountTimezone = accountUserSettings?.data.settingsOverride.timezone ?? DEFAULT_TIMEZONE

  const handleTimezoneSelected = async (selectedTimezone: string | null) => {
    if (accountTimezone === selectedTimezone) {
      return
    }
    if (!accountUserSettings) {
      return
    }
    await updateAccountUserSettings({
      accountId,
      payload: {
        overrideUserSettings: accountUserSettings.data.overrideUserSettings,
        settingsOverride: {
          timezone: selectedTimezone,
        },
      },
    }).unwrap()

    setShowChangesSaved(true)
    if (changesSavedTimeoutRef.current) {
      clearTimeout(changesSavedTimeoutRef.current)
    }
    changesSavedTimeoutRef.current = setTimeout(() => {
      setShowChangesSaved(false)
    }, 2000)
  }

  return (
    <Container>
      <Row className='zocks-mb-2'>
        <Col>
          <h4 className='zocks-text-gray-900 mb-2'>Time zone</h4>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          <p className='zocks-text-gray-900 p-0 m-0'>
            Set a default time zone for all users in your account. Zocks uses this when scheduling meetings, processing timestamps, and interpreting time
            references. Users can change their personal time zone in profile settings.
          </p>
        </Col>
      </Row>
      <Row className='mb-1'>
        <div className='paragraph-medium'>Default account time zone</div>
      </Row>
      <Row className='mb-4'>
        <Col className='pe-2'>
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <TimezoneSelector
              initialSelectedTimezone={accountTimezone}
              onTimezoneSelected={handleTimezoneSelected}
              loading={accountUserSettingsLoading && !accountTimezone}
              disabled={accountUserSettingsSaveLoading || accountUserSettingsLoading}
            />
          </ScopedBlock>
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col>
          <div className='d-flex align-items-center justify-content-between gap-3'>
            {accountTimezone && <CurrentTimeDisplay timezone={accountTimezone} />}
            {showChangesSaved ? (
              <div className='label-small text-success d-flex gap-1 align-items-center'>
                <i className='bi bi-check fs-6'></i>
                <span>Changes saved</span>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
